<template>
  <LocalFileUploaderModal
    :modalTitle="$i18nPresenter.t('Import slides')"
    :isModalVisible="isModalVisible"
    :staticState="staticState"
    :uploadingState="uploadingState"
    :convertingState="convertingState"
    :uploadError="importError"
    :featureName="UploadFeatureName.IMPORT_PPT_PDF"
    :needGenerateQuiz="needGenerateQuiz"
    :instructionText="instructionText"
    :customPropsForTracking="customPropsForTracking"
    @hide="hide()"
  >
    <template #footerSection>
      <div class="generate-from-doc__check-box-wrapper">
        <aha-checkbox
          v-model="needGenerateQuiz"
          :disabled="isDocToSlideCheckBoxDisabled"
          @change="onChangeDocToSlideCheckBox()"
        >
          {{ $i18nPresenter.t('Generate quiz from this document') }}
        </aha-checkbox>
        <AIDocToQuizSecurityTooltip />
      </div>
    </template>
  </LocalFileUploaderModal>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import Utils from '@/util'
import eventCategories from '@/constant/eventCategories'
import { IMPORT_DOCUMENT } from '@/constant/eventActions'
import importStatusConstant from '@/constant/importStatus'
import UploadFeatureName from '@/constant/uploadFeatureName'

// components
import LocalFileUploaderModal from '@/components-v2/modals/LocalFileUploaderModal.vue'
import AIDocToQuizSecurityTooltip from '@/components-v2/openAI/AIDocToQuizSecurityTooltip.vue'

export default {
  components: {
    LocalFileUploaderModal,
    AIDocToQuizSecurityTooltip,
  },
  data() {
    return {
      file: null,
      isModalVisible: false,
      needGenerateQuiz: !this.docToSlideUniqueRequestId,
      importStatusConstant,
      UploadFeatureName,
    }
  },
  computed: {
    ...mapGetters('docToSlideGenerator', ['docToSlideUniqueRequestId', 'isDocToSlideGenerating']),
    ...mapState({
      currentUser: (state) => state.currentUser,
      presentation: (state) => state.presentation,
      selectedFolderId: (state) => state.selections.selectedFolderId,
      isOnboardingV3Showing: (state) => state.indicators.isOnboardingV3Showing,
      importStatus: (state) => state.importStatus,
      importError: (state) => state.importError,
    }),
    gaCategory() {
      if (this.$route.path === '/apps/presentations' || this.isOnboardingV3Showing) {
        return eventCategories.PRESENTATION_LIST
      }
      return eventCategories.PRESENTATION_EDITOR
    },
    eventLabel() {
      return this.isOnboardingV3Showing ? 'Import from onboarding' : ''
    },
    eventValue() {
      return this.isOnboardingV3Showing ? 'Onboarding option' : ''
    },
    customPropsForTracking() {
      return {
        gaCategory: this.gaCategory,
        eventLabel: this.eventLabel,
        eventValue: this.eventValue,
      }
    },
    staticState() {
      return this.importStatus === 1
    },
    uploadingState() {
      return this.importStatus === 2
    },
    convertingState() {
      return this.importStatus === 3
    },
    isDocToSlideCheckBoxDisabled() {
      return this.isDocToSlideGenerating || this.uploadingState || this.convertingState
    },
    instructionText() {
      return this.$i18nPresenter.t('Upload a PDF, PPT or PPTX file. Up to 50MB and 100 slides.')
    },
  },
  mounted() {
    this.show()
  },
  methods: {
    ...mapActions('docToSlideGenerator', ['setDocToSlideImportStageAction', 'generateDocToSlideUniqueRequestIdAction']),

    clickBrowseFile() {
      return Utils.mixpanelTracking(this.gaCategory, 'Import > Browse file', this.eventLabel, this.eventValue)
    },
    show() {
      this.isModalVisible = true
    },
    hide() {
      this.$emit('hide')
      this.$store.state.importError = null
      this.$store.state.openImportModal = false
      Utils.mixpanelTracking(this.gaCategory, 'Import > Close modal', this.eventLabel, this.eventValue)
    },
    onChangeDocToSlideCheckBox() {
      const eventAction = `${IMPORT_DOCUMENT} > Generate quiz from this document`
      const trackData = {
        eventAction,
        eventCategory: eventCategories.PRESENTATION_EDITOR,
        eventLabel: this.needGenerateQuiz ? 'enable' : 'disable',
        presentationId: this.presentation.id,
        userId: this.currentUser.id,
      }
      Utils.trackGA4AndMixpanel(eventAction, trackData)
    },
  },
}
</script>

<style lang="scss" scoped>
.tab-content {
  @apply border-2 border-dashed border-primary-90 px-4;
  @apply bg-primary-60 rounded py-8;
  @apply relative text-center;
}
.custom-file {
  @apply absolute inset-0 overflow-hidden opacity-0;
  & ::v-deep .custom-file-input {
    @apply w-full  h-full cursor-pointer;
  }
}
.generate-from-doc__check-box-wrapper {
  @apply pt-4;
  display: flex;
  &::v-deep {
    .generate-from-doc__tooltip.aha-custom-tooltip.top {
      top: 8.25rem;
      left: 2.5rem;
      &::after {
        left: 43%;
      }
    }
  }
}
</style>
